import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  InputAdornment,
  CircularProgress,
  Fade,
  List,
  ListItem,
  Avatar,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  AutoAwesome as MentorIcon,
  Close as CloseIcon,
  Language as WebIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

const MentorSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSearch = async () => {
    if (!query.trim()) return;

    const userMessage = { role: 'user', content: query };
    setMessages((prev) => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const response = await fetch('/api/mentor/answer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`, // Ensure token is passed
        },
        body: JSON.stringify({ query: userMessage.content }),
      });

      const data = await response.json();

      if (data.error) {
        setMessages((prev) => [
          ...prev,
          { role: 'mentor', content: `Error: ${data.error}`, isError: true },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { 
            role: 'mentor', 
            content: data.answer, 
            source: data.source,
            timestamp: data.timestamp 
          },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'mentor', content: "I'm having trouble connecting right now. Please try again.", isError: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: { xs: '90vw', sm: 400 },
        height: { xs: '80vh', sm: 500 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        zIndex: 1300,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MentorIcon />
          <Typography variant="subtitle1" fontWeight="bold">
            The Mentor
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default' }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.7 }}>
            <MentorIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
            <Typography variant="body2">
              Ask me about exam dates, recruitment notices, or study topics.
            </Typography>
            <Typography variant="caption" display="block">
              I search the web to give you verified answers.
            </Typography>
          </Box>
        )}
        
        <List sx={{ p: 0 }}>
          {messages.map((msg, index) => (
            <ListItem
              key={index}
              sx={{
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                px: 0,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  maxWidth: '85%',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user' ? 'primary.light' : 'background.paper',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  boxShadow: 1,
                  border: msg.role === 'mentor' ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <Typography variant="body2" sx={{ mb: 0.5 }}>{children}</Typography>,
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
                
                {msg.source === 'grounded_llm' && (
                  <Tooltip title="Verified via Web Search">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, opacity: 0.8 }}>
                      <WebIcon sx={{ fontSize: 12 }} />
                      <Typography variant="caption" sx={{ fontSize: 9 }}>Grounded Source</Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </ListItem>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>

      {/* Input */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask anything..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          disabled={loading}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton onClick={handleSearch} color="primary" size="small">
                    <SendIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
        />
      </Box>
    </Paper>
  );
};

export default MentorSearch;
